import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import { useDeleteAgent } from '@/hooks/agents/useDeleteAgent';
import { deleteAgentDescription } from '@/lib/agent-delete';
import type { Agent } from '@/lib/types';

export interface DeleteAgentDialogProps {
  agent: Agent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation for deleting an agent.
 *
 * Delete is the only irreversible action in the product: it removes the run
 * *and* tears its sandbox down, and neither comes back. Every surface that
 * offers it (the stream header menu, the detail panel, the sidebar row menu,
 * the inbox) renders this dialog, so the question, the wording, the mutation,
 * and leaving the deleted run's view are the same everywhere. Its props match
 * the agent-action dialog contract, which is what lets it drop straight into
 * `useAgentActions`.
 */
export function DeleteAgentDialog({ agent, open, onOpenChange }: DeleteAgentDialogProps) {
  const deleteAgent = useDeleteAgent();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete this agent?"
      description={deleteAgentDescription(agent)}
      confirmLabel="Delete"
      // `useDeleteAgent` sends the delete and, when this is the run the URL has
      // open, navigates off it. The mutation owns the result toasts: confirming
      // closes (and unmounts) this dialog, so callbacks passed to `mutate` here
      // would never run.
      onConfirm={() => deleteAgent(agent.id)}
    />
  );
}
